import { useMemo } from 'react';
import { Button, useI18n } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import * as web3 from '@ywwwtseng/web3';
import * as utils from '@/utils';
import { useToken } from '@/hooks/useToken';

interface CreateGiftConfirmProps {
  token_id: string;
  platform_fee_percent: number;
  chat_fee_percent: number;
  amount: string;
  isLoading: boolean;
  onConfirm: () => void;
}

export function CreateGiftConfirm({
  token_id,
  platform_fee_percent,
  chat_fee_percent,
  amount,
  isLoading,
  onConfirm,
}: CreateGiftConfirmProps) {
  const { t } = useI18n();
  const token = useToken(token_id);
  const tokenSymbol = useMemo(() => {
    return utils.token.getTokenSymbol(token_id);
  }, [token_id]);

  if (!token) {
    return null;
  }

  const { platform_fee, chat_fee, final_payable } = utils.gift.fees.calculate({
    fees: {
      platform_fee_percent,
      chat_fee_percent,
    },
    amount: web3.utils.parseUnits(amount, token.decimals).toString(),
  });

  return (
    <>
      <Typography size="3" i18n="create_gift_confirm.title" />
      <div className="flex flex-col w-full gap-1 px-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <Typography size="1" i18n="gift.total_amount" />
          <Typography size="1">
            {amount} {tokenSymbol}
          </Typography>
        </div>
        <div className="flex items-center justify-between">
          <Typography size="1" i18n="create_gift_confirm.platform_fee" />
          <Typography size="1">
            {web3.utils.formatUnits(platform_fee, token.decimals)} {tokenSymbol}
            ({platform_fee_percent * 100}%)
          </Typography>
        </div>
        {chat_fee !== '0' && (
          <div className="flex items-center justify-between">
            <Typography size="1" i18n="create_gift_confirm.chat_fee" />
            <Typography size="1">
              {web3.utils.formatUnits(chat_fee, token.decimals)} {tokenSymbol}(
              {chat_fee_percent * 100}
              %)
            </Typography>
          </div>
        )}

        <div className="flex items-center justify-between my-2">
          <Typography size="1" i18n="create_gift_confirm.final_payable" />
          <Typography size="1">
            {web3.utils.formatUnits(final_payable, token.decimals)}{' '}
            {utils.token.getTokenSymbol(token_id)}
          </Typography>
        </div>

        <Button
          isLoading={isLoading}
          width="full"
          color="primary"
          variant="contained"
          size="sm"
          rounded="full"
          onClick={onConfirm}
        >
          {t('common.submit')}
        </Button>
      </div>
    </>
  );
}
